/**
 * BatchManagement.js
 * Tab 1 – Batches  (CRUD: name, level, program from DB, schedule days, times)
 * Tab 2 – Enroll   (pick batch → search student → one-click enroll)
 * Tab 3 – Change Log
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  FaPlus, FaEdit, FaTrash, FaSave, FaTimes,
  FaUsers, FaClock, FaLayerGroup, FaHistory,
  FaCheckCircle, FaSearch, FaChevronRight,
  FaUserPlus, FaExclamationCircle, FaArrowLeft,
  FaCalendarAlt, FaTag,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useTheme, getT } from "./ThemeContext";
import API_BASE from "./apiConfig";
const BASE = API_BASE;
const STATIC = {
  blue:   Colors.primaryBlue || "#034e7b",
  dark:   "#02304f",
  yellow: Colors.logoYellow  || "#f7ce0b",
  font:   "'Segoe UI', system-ui, sans-serif",
  blur:   "blur(20px)",
};
const card = (T) => ({
  background:     T.glass,
  backdropFilter: STATIC.blur, WebkitBackdropFilter: STATIC.blur,
  borderRadius:   14,
  boxShadow:      T.glassSh,
  border:         T.glassBdr,
});
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat"];
const LEVEL_COLORS = {
  Beginner: "#3b82f6", Intermediate: "#8b5cf6",
  Advanced: "#10b981", Elite: "#f59e0b",
};

function fmtTime(t) {
  if (!t) return "—";
  const [h, m] = t.split(":");
  const h12 = +h % 12 || 12;
  return `${h12}:${m} ${+h < 12 ? "AM" : "PM"}`;
}
function lc4(batch) {
  return batch.color_hex || LEVEL_COLORS[batch.level_name] || STATIC.blue;
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg, onClose }) {
  if (!msg) return null;
  return (
    <div style={{
      padding: "11px 16px", borderRadius: 10, marginBottom: 16,
      fontSize: 13, fontWeight: 600, fontFamily: STATIC.font,
      display: "flex", alignItems: "center", gap: 10,
      background: msg.ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
      color: msg.ok ? "#34d399" : "#f87171",
      border: `1px solid ${msg.ok ? "rgba(52,211,153,0.30)" : "rgba(248,113,113,0.30)"}`,
    }}>
      {msg.ok ? <FaCheckCircle size={13}/> : <FaExclamationCircle size={13}/>}
      <span style={{ flex: 1 }}>{msg.text}</span>
      <FaTimes size={11} style={{ cursor: "pointer", WebkitTapHighlightColor:"transparent", opacity: 0.5 }} onClick={onClose}/>
    </div>
  );
}

// ── Form label + input ────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div>
      <label style={{
        fontSize: 11, fontWeight: 700, color: T.textLabel, textTransform: "uppercase",
        letterSpacing: "0.07em", display: "block", marginBottom: 5, fontFamily: STATIC.font,
      }}>
        {label}{hint && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6, fontSize: 10 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}
function Inp({ label, hint, value, onChange, type = "text", placeholder, min, max }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <Field label={label} hint={hint}>
      <input type={type} value={value} onChange={onChange}
        placeholder={placeholder} min={min} max={max}
        style={{
          width: "100%", padding: "9px 12px", borderRadius: 9,
          border: `1.5px solid ${T.inputBdr}`, fontSize: 13, background: T.inputBg, color: T.inputText,
          fontFamily: STATIC.font, boxSizing: "border-box", outline: "none", colorScheme: isDark ? "dark" : "light",
        }}/>
    </Field>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — BATCHES CRUD
// ══════════════════════════════════════════════════════════════════════════════
const BLANK = {
  batch_name: "", level_id: "", program_id: "",
  weekday_start: "17:00", weekday_end: "18:30",
  has_saturday: false, saturday_start: "09:00", saturday_end: "11:00",
  max_capacity: 30, description: "", schedule_days: [],
};

function BatchesTab({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [batches, setBatches]   = useState([]);
  const [levels, setLevels]     = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm]         = useState(BLANK);
  const [editId, setEditId]     = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const formRef = React.useRef(null);

  const load = useCallback(() => {
    fetch(`${BASE}/batches/list`).then(r => r.json()).then(d => setBatches(d || [])).catch(() => {});
    fetch(`${BASE}/batches/skill-levels`).then(r => r.json()).then(d => setLevels(d || [])).catch(() => {});
    // Programs come from Config Master — /config/lookups/programs — so any change there reflects here immediately
    fetch(`${BASE}/config/lookups/programs`).then(r => r.json()).then(d => setPrograms((d.data || []).filter(p => p.active !== 0 && p.active !== false))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const selLevel = levels.find(l => String(l.level_id) === String(form.level_id));
  const formColor = selLevel ? (selLevel.color_hex || LEVEL_COLORS[selLevel.level_name] || STATIC.blue) : STATIC.blue;

  const handleNew = () => {
    setForm({ ...BLANK, level_id: levels[0]?.level_id || "", program_id: programs[0]?.program_id || "" });
    setEditId(null); setShowForm(true); setMsg(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const handleEdit = b => {
    setForm({
      batch_name: b.batch_name, level_id: String(b.level_id),
      program_id: String(b.program_id || programs[0]?.program_id || ""),
      weekday_start: b.weekday_start?.slice(0, 5) || "17:00",
      weekday_end:   b.weekday_end?.slice(0, 5)   || "18:30",
      has_saturday:  !!b.saturday_start,
      saturday_start: b.saturday_start?.slice(0, 5) || "09:00",
      saturday_end:   b.saturday_end?.slice(0, 5)   || "11:00",
      max_capacity: b.max_capacity,
      description: b.description || "",
      schedule_days: b.schedule_days || [],
    });
    setEditId(b.batch_id); setShowForm(true); setMsg(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const selProgram = programs.find(p => String(p.program_id) === String(form.program_id));
  const requiredDays = selProgram?.days_per_week || 0;
  const dayCountOk = !selProgram || form.schedule_days.length === requiredDays;

  const handleSave = async () => {
    if (!form.batch_name || !form.level_id) { setMsg({ ok: false, text: "Batch name and level are required." }); return; }
    if (!form.program_id) { setMsg({ ok: false, text: "Please select a program for this batch." }); return; }
    if (!dayCountOk) {
      setMsg({ ok: false, text: `${selProgram.program_name} requires exactly ${requiredDays} day${requiredDays > 1 ? "s" : ""}. You selected ${form.schedule_days.length}.` });
      return;
    }
    setSaving(true); setMsg(null);
    const url = editId ? `${BASE}/batches/${editId}` : `${BASE}/batches/create`;
    const r = await fetch(url, {
      method: editId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        level_id: parseInt(form.level_id),
        program_id: parseInt(form.program_id),
        updated_by: String(currentUserId || "system"),
      }),
    });
    const d = await r.json(); setSaving(false);
    if (r.ok) { setMsg({ ok: true, text: editId ? "Batch updated!" : "Batch created!" }); setShowForm(false); load(); }
    else setMsg({ ok: false, text: d.detail || "Error saving batch." });
  };

  const handleDelete = async id => {
    if (!window.confirm("Deactivate this batch?")) return;
    await fetch(`${BASE}/batches/${id}?updated_by=${currentUserId || "system"}`, { method: "DELETE" });
    load();
  };

  const handleProgramSelect = pid => {
    setForm(f => ({ ...f, program_id: String(pid), schedule_days: [] }));
    setMsg(null);
  };

  const toggleDay = d => {
    if (!selProgram) { setMsg({ ok: false, text: "Select a program first to know how many days are allowed." }); return; }
    setForm(f => {
      const days = f.schedule_days;
      if (days.includes(d)) {
        // always allow deselect
        return { ...f, schedule_days: days.filter(x => x !== d) };
      }
      if (days.length >= requiredDays) {
        // at limit — do nothing, show hint via msg
        return f;
      }
      return { ...f, schedule_days: [...days, d] };
    });
    setMsg(null);
  };

  const grouped = levels.reduce((acc, lv) => {
    acc[lv.level_name] = batches.filter(b => b.level_name === lv.level_name);
    return acc;
  }, {});

  return (
    <div>
      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
        <button onClick={handleNew} style={{
          padding: "10px 22px", borderRadius: 10, border: "none", cursor: "pointer", WebkitTapHighlightColor:"transparent",
          fontFamily: STATIC.font, background: `linear-gradient(135deg,${STATIC.yellow},#ffe066)`,
          color: STATIC.blue, fontSize: 13, fontWeight: 800,
          display: "flex", alignItems: "center", gap: 7,
          boxShadow: "0 4px 14px rgba(255,200,0,0.28)",
        }}>
          <FaPlus size={11}/> New Batch
        </button>
      </div>

      <Toast msg={msg} onClose={() => setMsg(null)}/>

      {/* ── Batch Form ── */}
      {showForm && (
        <div ref={formRef} style={{ ...card(T), padding: 28, marginBottom: 24, borderTop: `3px solid ${formColor}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: STATIC.blue, fontFamily: STATIC.font }}>
              {editId ? "Edit Batch" : "Create New Batch"}
            </h3>
            <button onClick={() => setShowForm(false)} style={{
              width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(3,78,123,0.14)",
              background: "transparent", color: T.textMuted, cursor: "pointer", WebkitTapHighlightColor:"transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FaTimes size={13}/>
            </button>
          </div>

          {/* Row 1: Name + Level */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <Inp label="Batch Name *" value={form.batch_name}
              onChange={e => setForm(f => ({ ...f, batch_name: e.target.value }))}
              placeholder="e.g. Beginner Batch 1"/>
            <Field label="Level *">
              <select value={form.level_id}
                onChange={e => setForm(f => ({ ...f, level_id: e.target.value }))}
                style={{
                  width: "100%", padding: "9px 12px", borderRadius: 9,
                  border: `1.5px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText,
                  fontSize: 13, fontFamily: STATIC.font, boxSizing: "border-box",
                  colorScheme: isDark ? "dark" : "light" }}>
                <option value="">— Select Level —</option>
                {levels.map(l => <option key={l.level_id} value={l.level_id}>{l.level_name}</option>)}
              </select>
            </Field>
          </div>

          {/* Row 2: Program (cards from DB — managed in Config Master → Lookup Values → Programs) */}
          <Field label="Program *" hint="days/week and sessions/month are set in Config Master → Lookup Values → Programs">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 8, marginTop: 2 }}>
              {programs.length === 0 && (
                <div style={{ gridColumn:"1/-1", padding:"14px 16px", borderRadius:10, background:"rgba(255,200,0,0.06)", border:"1.5px dashed rgba(255,200,0,0.45)", fontSize:12, color:T.textMuted, fontFamily:STATIC.font }}>
                  No active programs found. Add programs in <strong style={{ color:STATIC.blue }}>Config Master → Lookup Values → Programs</strong>.
                </div>
              )}
              {programs.map(p => {
                const active = String(form.program_id) === String(p.program_id);
                return (
                  <div key={p.program_id}
                    onClick={() => handleProgramSelect(p.program_id)}
                    style={{
                      padding: "12px 14px", borderRadius: 10, cursor: "pointer", WebkitTapHighlightColor:"transparent",
                      border: `2px solid ${active ? formColor : T.inputBdr}`,
                      background: active ? formColor + "18" : T.glass,
                      display: "flex", alignItems: "center", gap: 10,
                      transition: "all 0.15s",
                    }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                      background: active ? formColor + "22" : "rgba(3,78,123,0.06)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ fontSize: 16, fontWeight: 900, color: active ? formColor : STATIC.blue }}>
                        {p.days_per_week}×
                      </span>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: active ? formColor : STATIC.blue, fontFamily: STATIC.font }}>
                        {p.program_name}
                      </div>
                      <div style={{ fontSize: 10, color: T.textMuted }}>{p.sessions_per_month} sessions/mo</div>
                      {p.description && (
                        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2, fontStyle: "italic" }}>{p.description}</div>
                      )}
                    </div>
                    {active && <FaCheckCircle size={13} color={formColor} style={{ marginLeft: "auto", flexShrink: 0 }}/>}
                  </div>
                );
              })}
            </div>
          </Field>

          {/* Row 3: Schedule Days */}
          <div style={{ marginTop: 20, marginBottom: 20 }}>
            <Field label="Schedule Days" hint="training days — copied to enrollments">
              <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                {selProgram ? (
                  <>
                    <span style={{
                      fontSize: 12, fontWeight: 700, fontFamily: STATIC.font,
                      color: dayCountOk ? "#059669" : form.schedule_days.length > requiredDays ? "#dc2626" : STATIC.blue,
                    }}>
                      {form.schedule_days.length} / {requiredDays} selected
                    </span>
                    {!dayCountOk && (
                      <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>
                        — {selProgram.program_name} needs exactly {requiredDays} day{requiredDays > 1 ? "s" : ""}
                      </span>
                    )}
                    {dayCountOk && form.schedule_days.length > 0 && (
                      <span style={{ fontSize: 11, color: "#059669", fontWeight: 600 }}>✓ Good</span>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: 11, color: T.textMuted }}>Select a program above first</span>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                {DOW.map((d, i) => {
                  const on = form.schedule_days.includes(i);
                  const atLimit = !on && selProgram && form.schedule_days.length >= requiredDays;
                  const noProgram = !selProgram;
                  const disabled = atLimit || noProgram;
                  return (
                    <button key={i} onClick={() => toggleDay(i)}
                      disabled={disabled && !on}
                      title={atLimit ? `${selProgram.program_name} only allows ${requiredDays} day${requiredDays > 1 ? "s" : ""}` : ""}
                      style={{
                        padding: "9px 0", width: 48, borderRadius: 9, fontSize: 12, fontWeight: 800,
                        cursor: disabled && !on ? "not-allowed" : "pointer",
                        border: `2px solid ${on ? formColor : "rgba(3,78,123,0.14)"}`,
                        background: on ? formColor : disabled ? T.glass : T.glass,
                        color: on ? "#fff" : disabled ? "#cbd5e1" : T.textMuted,
                        transition: "all 0.15s",
                        opacity: disabled && !on ? 0.5 : 1,
                      }}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          {/* Row 4: Times + Capacity */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Inp label="Weekday Start" type="time" value={form.weekday_start}
              onChange={e => setForm(f => ({ ...f, weekday_start: e.target.value }))}/>
            <Inp label="Weekday End"   type="time" value={form.weekday_end}
              onChange={e => setForm(f => ({ ...f, weekday_end: e.target.value }))}/>
            <Inp label="Max Capacity"  type="number" min={1} max={200} value={form.max_capacity}
              onChange={e => setForm(f => ({ ...f, max_capacity: e.target.value }))}/>
            <Inp label="Description" placeholder="Optional notes" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}/>
          </div>

          {/* Saturday */}
          <div style={{ marginBottom: form.has_saturday ? 14 : 20 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", WebkitTapHighlightColor:"transparent", fontSize: 13, fontFamily: STATIC.font, color: STATIC.blue, fontWeight: 600 }}>
              <input type="checkbox" checked={form.has_saturday}
                onChange={e => setForm(f => ({ ...f, has_saturday: e.target.checked }))}/>
              Has Saturday session
            </label>
          </div>
          {form.has_saturday && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <Inp label="Saturday Start" type="time" value={form.saturday_start}
                onChange={e => setForm(f => ({ ...f, saturday_start: e.target.value }))}/>
              <Inp label="Saturday End" type="time" value={form.saturday_end}
                onChange={e => setForm(f => ({ ...f, saturday_end: e.target.value }))}/>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setShowForm(false)} style={{
              padding: "10px 22px", borderRadius: 9, border: "1px solid rgba(3,78,123,0.16)",
              background: "transparent", fontSize: 13, fontWeight: 700,
              cursor: "pointer", WebkitTapHighlightColor:"transparent", color: T.textMuted, fontFamily: STATIC.font,
            }}>Cancel</button>
            <button onClick={handleSave} disabled={saving || !dayCountOk} style={{
              padding: "10px 24px", borderRadius: 9, border: "none",
              cursor: saving || !dayCountOk ? "not-allowed" : "pointer",
              fontFamily: STATIC.font,
              background: !dayCountOk ? "rgba(3,78,123,0.08)" : `linear-gradient(135deg,${STATIC.yellow},#ffe066)`,
              color: !dayCountOk ? "#aab0be" : STATIC.blue,
              fontSize: 13, fontWeight: 800,
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: !dayCountOk ? "none" : "0 3px 12px rgba(255,200,0,0.28)",
            }}>
              <FaSave size={12}/> {saving ? "Saving…" : editId ? "Update Batch" : "Create Batch"}
            </button>
          </div>
        </div>
      )}

      {/* ── Batch Cards ── */}
      {levels.map(lv => {
        const lvB = grouped[lv.level_name] || [];
        if (!lvB.length) return null;
        const lc = lv.color_hex || LEVEL_COLORS[lv.level_name] || STATIC.blue;
        return (
          <div key={lv.level_id} style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: lc }}/>
              <span style={{ fontSize: 15, fontWeight: 800, color: lc, fontFamily: STATIC.font }}>{lv.level_name}</span>
              <span style={{ fontSize: 11, color: T.textMuted, background: `${lc}15`, padding: "2px 10px", borderRadius: 12 }}>
                {lvB.length} batch{lvB.length !== 1 ? "es" : ""}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(310px,1fr))", gap: 16 }}>
              {lvB.map(b => {
                const prog = programs.find(p => p.program_id === b.program_id);
                const pct = b.max_capacity > 0 ? Math.round((b.enrolled_count / b.max_capacity) * 100) : 0;
                return (
                  <div key={b.batch_id} style={{ ...card(T), padding: 20, borderTop: `3px solid ${lc}` }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: STATIC.blue, fontFamily: STATIC.font }}>{b.batch_name}</div>
                        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{b.description || "No description"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0, marginLeft: 8 }}>
                        <button onClick={() => handleEdit(b)} style={{
                          background: `${STATIC.blue}11`, border: "none", borderRadius: 7,
                          padding: "5px 9px", cursor: "pointer", WebkitTapHighlightColor:"transparent", color: STATIC.blue,
                        }}><FaEdit size={12}/></button>
                        <button onClick={() => handleDelete(b.batch_id)} style={{
                          background: "#fee2e2", border: "none", borderRadius: 7,
                          padding: "5px 9px", cursor: "pointer", WebkitTapHighlightColor:"transparent", color: "#ef4444",
                        }}><FaTrash size={12}/></button>
                      </div>
                    </div>

                    {/* Program badge */}
                    {prog && (
                      <div style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "3px 10px", borderRadius: 20, background: `${lc}14`, marginBottom: 10,
                      }}>
                        <FaTag size={9} color={lc}/>
                        <span style={{ fontSize: 11, fontWeight: 700, color: lc, fontFamily: STATIC.font }}>{prog.program_name}</span>
                        <span style={{ fontSize: 10, color: T.textMuted }}>· {prog.sessions_per_month} sessions/mo</span>
                      </div>
                    )}

                    {/* Times */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                      <div style={{ background: "rgba(3,78,123,0.05)", borderRadius: 8, padding: "8px 12px" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Weekday</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: STATIC.blue, marginTop: 2 }}>
                          {fmtTime(b.weekday_start)} – {fmtTime(b.weekday_end)}
                        </div>
                      </div>
                      {b.has_saturday && (
                        <div style={{ background: "rgba(3,78,123,0.05)", borderRadius: 8, padding: "8px 12px" }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Saturday</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: STATIC.blue, marginTop: 2 }}>
                            {fmtTime(b.saturday_start)} – {fmtTime(b.saturday_end)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Days */}
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
                      {(b.schedule_days || []).map(d => (
                        <span key={d} style={{
                          padding: "2px 10px", borderRadius: 12, fontSize: 11,
                          fontWeight: 700, background: `${lc}18`, color: lc,
                        }}>{DOW[d]}</span>
                      ))}
                      {!b.schedule_days?.length && <span style={{ fontSize: 12, color: T.textMuted, fontStyle: "italic" }}>No days set</span>}
                    </div>

                    {/* Capacity */}
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <FaUsers size={10}/> {b.enrolled_count || 0} enrolled
                        </span>
                        <span>{b.max_capacity} capacity</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                        <div style={{
                          width: `${Math.min(pct, 100)}%`, height: "100%", borderRadius: 3,
                          background: pct >= 100 ? "#dc2626" : pct >= 80 ? "#d97706" : lc,
                        }}/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {batches.length === 0 && !showForm && (
        <div style={{ ...card(T), padding: 48, textAlign: "center" }}>
          <FaLayerGroup size={36} style={{ color: T.textMuted, opacity: 0.3, display: "block", margin: "0 auto 14px" }}/>
          <p style={{ color: T.textMuted, fontSize: 14, fontFamily: STATIC.font }}>No batches yet. Click <strong>New Batch</strong> to get started.</p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — ENROLL STUDENTS
// ══════════════════════════════════════════════════════════════════════════════
function EnrollTab({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [batches, setBatches] = useState([]);
  const [levels, setLevels]   = useState([]);
  const [active, setActive]   = useState(null);

  const loadBatches = useCallback(() => {
    fetch(`${BASE}/batches/list`).then(r => r.json()).then(d => setBatches(d || [])).catch(() => {});
    fetch(`${BASE}/batches/skill-levels`).then(r => r.json()).then(d => setLevels(d || [])).catch(() => {});
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  if (active) {
    return (
      <EnrollPanel
        batch={active}
        currentUserId={currentUserId}
        onBack={() => { setActive(null); loadBatches(); }}
      />
    );
  }

  const grouped = levels.reduce((acc, lv) => {
    acc[lv.level_name] = batches.filter(b => b.level_name === lv.level_name);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: STATIC.blue, fontFamily: STATIC.font }}>
          Select a Batch
        </h3>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: T.textMuted, fontFamily: STATIC.font }}>
          Click a batch to add students — program and schedule are inherited automatically
        </p>
      </div>

      {levels.map(lv => {
        const lvB = grouped[lv.level_name] || [];
        if (!lvB.length) return null;
        const lc = lv.color_hex || LEVEL_COLORS[lv.level_name] || STATIC.blue;
        return (
          <div key={lv.level_id} style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: lc }}/>
              <span style={{ fontSize: 14, fontWeight: 800, color: lc, fontFamily: STATIC.font }}>{lv.level_name}</span>
              <span style={{ fontSize: 11, color: T.textMuted, background: `${lc}15`, padding: "2px 8px", borderRadius: 10 }}>
                {lvB.length} batch{lvB.length !== 1 ? "es" : ""}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 12 }}>
              {lvB.map(b => {
                const pct = b.max_capacity > 0 ? Math.round((b.enrolled_count / b.max_capacity) * 100) : 0;
                const full = b.enrolled_count >= b.max_capacity;
                return (
                  <div key={b.batch_id}
                    onClick={() => !full && setActive(b)}
                    style={{
                      ...card(T), padding: 18, cursor: full ? "not-allowed" : "pointer",
                      borderLeft: `4px solid ${lc}`, opacity: full ? 0.6 : 1,
                      transition: "box-shadow 0.15s, transform 0.15s",
                    }}
                    onMouseEnter={e => { if (!full) { e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,40,100,0.14)"; e.currentTarget.style.transform = "translateY(-2px)"; } }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,40,100,0.09)"; e.currentTarget.style.transform = "none"; }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: STATIC.blue, fontFamily: STATIC.font }}>{b.batch_name}</div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                          <FaClock size={9} style={{ marginRight: 4 }}/>{fmtTime(b.weekday_start)} – {fmtTime(b.weekday_end)}
                        </div>
                      </div>
                      {full
                        ? <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: 8, flexShrink: 0 }}>FULL</span>
                        : <FaChevronRight size={12} style={{ color: T.textMuted, marginTop: 3, flexShrink: 0 }}/>
                      }
                    </div>

                    {/* Program */}
                    {b.program_name && (
                      <div style={{ fontSize: 11, color: lc, fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                        <FaTag size={9}/> {b.program_name}
                      </div>
                    )}

                    {/* Days */}
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                      {(b.schedule_days || []).map(d => (
                        <span key={d} style={{
                          padding: "2px 8px", borderRadius: 10, fontSize: 10,
                          fontWeight: 700, background: `${lc}18`, color: lc,
                        }}>{DOW[d]}</span>
                      ))}
                    </div>

                    {/* Capacity bar */}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
                      <span>{b.enrolled_count} enrolled</span>
                      <span>{b.max_capacity} capacity</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                      <div style={{
                        width: `${Math.min(pct, 100)}%`, height: "100%", borderRadius: 3,
                        background: pct >= 100 ? "#dc2626" : pct >= 80 ? "#d97706" : lc,
                        transition: "width 0.4s",
                      }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {batches.length === 0 && (
        <div style={{ ...card(T), padding: 48, textAlign: "center" }}>
          <FaLayerGroup size={32} style={{ color: T.textMuted, opacity: 0.25, display: "block", margin: "0 auto 12px" }}/>
          <p style={{ color: T.textMuted, fontSize: 14, fontFamily: STATIC.font }}>
            No batches yet. Create batches in the <strong>Batches</strong> tab first.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Enroll Panel (batch selected — search & enroll, no program/days choice) ──
function EnrollPanel({ batch, currentUserId, onBack }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [search, setSearch]             = useState("");
  const [results, setResults]           = useState([]);
  const [searching, setSearching]       = useState(false);
  const [searchDone, setSearchDone]     = useState(false);
  const [enrollments, setEnrollments]   = useState([]);
  const [saving, setSaving]             = useState(null);
  const [msg, setMsg]                   = useState(null);
  const lc = lc4(batch);
  const searchRef = React.useRef(null);

  React.useEffect(() => { if (searchRef.current) searchRef.current.focus(); }, []);

  const loadEnr = useCallback(() => {
    fetch(`${BASE}/batches/enrollments/list?batch_id=${batch.batch_id}`)
      .then(r => r.json())
      .then(d => setEnrollments(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [batch.batch_id]);

  // Auto-load unenrolled students matching this batch level on mount
  const loadUnenrolled = useCallback(async (q) => {
    setSearching(true); setSearchDone(false);
    try {
      const url = `${BASE}/batches/enrollments/unenrolled?batch_id=${batch.batch_id}`
        + (q ? `&search=${encodeURIComponent(q)}` : "");
      const r = await fetch(url);
      const d = await r.json();
      setResults(Array.isArray(d) ? d : []);
      setSearchDone(true);
    } catch { setResults([]); setSearchDone(true); }
    finally { setSearching(false); }
  }, [batch.batch_id]);

  useEffect(() => { loadEnr(); loadUnenrolled(""); }, [loadEnr, loadUnenrolled]);

  const doSearch = async () => {
    loadUnenrolled(search.trim());
  };

  const enroll = async student => {
    setSaving(student.student_id); setMsg(null);
    try {
      const r = await fetch(`${BASE}/batches/enrollments/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: student.student_id,
          batch_id:   batch.batch_id,
          enrolled_by: String(currentUserId || "system"),
        }),
      });
      const d = await r.json();
      if (r.ok) {
        const warn = d.multi_batch_warning;
        setMsg({
          ok: true,
          text: warn
            ? `⚠ ${student.first_name} enrolled — also active in another batch.`
            : `✓ ${student.first_name} ${student.last_name} enrolled!`,
        });
        loadEnr();
        // Clear search and reload fresh level-matched list — prevents stale results
        setSearch("");
        loadUnenrolled("");
      } else {
        setMsg({ ok: false, text: d.detail || "Enrollment failed." });
      }
    } catch { setMsg({ ok: false, text: "Network error." }); }
    finally { setSaving(null); }
  };

  const unenroll = async (eid, name) => {
    if (!window.confirm(`Remove ${name} from ${batch.batch_name}?`)) return;
    await fetch(`${BASE}/batches/enrollments/${eid}?updated_by=${currentUserId || "system"}`, { method: "DELETE" });
    loadEnr();
    loadUnenrolled(search.trim()); // student reappears on left after removal
  };

  const enrolledIds = new Set(enrollments.map(e => e.student_id));

  return (
    <div>
      {/* ── Back bar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "7px 14px",
          borderRadius: 9,
          background: T.glass, color: T.textSub, fontSize: 12, fontWeight: 700,
          border: `1.5px solid ${T.inputBdr}`,
          cursor: "pointer", WebkitTapHighlightColor:"transparent", fontFamily: STATIC.font,
        }}>
          <FaArrowLeft size={11}/> All Batches
        </button>
        <div style={{ height: 28, width: 1, background: "rgba(3,78,123,0.12)" }}/>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: lc + "22", border: `2px solid ${lc}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FaUsers size={15} color={lc}/>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, fontFamily: STATIC.font }}>{batch.batch_name}</div>
          <div style={{ fontSize: 12, color: T.textMuted, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
            <span style={{ background: lc + "22", color: lc, fontWeight: 700, padding: "1px 8px", borderRadius: 8, fontSize: 11 }}>{batch.level_name}</span>
            {batch.program_name && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FaTag size={9}/> {batch.program_name}
              </span>
            )}
            {(batch.schedule_days || []).length > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <FaCalendarAlt size={9}/> {batch.schedule_days.map(d => DOW[d]).join(", ")}
              </span>
            )}
            <span>{enrollments.length} / {batch.max_capacity} enrolled</span>
          </div>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 20, alignItems: "start" }}>

        {/* LEFT: search + enroll */}
        <div style={{ ...card(T), padding: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: STATIC.blue, textTransform: "uppercase", letterSpacing: "0.10em", marginBottom: 16, display: "flex", alignItems: "center", gap: 7 }}>
            <FaUserPlus size={12}/> Add Students
          </div>

          <Toast msg={msg} onClose={() => setMsg(null)}/>

          {/* Batch info pill */}
          <div style={{ background: `${lc}0a`, border: `1.5px solid ${lc}22`, borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: lc, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Enrolling into</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: STATIC.blue, fontFamily: STATIC.font }}>{batch.batch_name}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {batch.program_name && <span><FaTag size={9}/> {batch.program_name}</span>}
              <span><FaClock size={9}/> {fmtTime(batch.weekday_start)}–{fmtTime(batch.weekday_end)}</span>
              {(batch.schedule_days || []).length > 0 && (
                <span><FaCalendarAlt size={9}/> {batch.schedule_days.map(d => DOW[d]).join(", ")}</span>
              )}
            </div>
          </div>

          {/* Search input */}
          <div style={{ position: "relative", marginBottom: 6 }}>
            <input
              ref={searchRef} value={search}
              onChange={e => {
                setSearch(e.target.value);
                if (!e.target.value.trim()) { loadUnenrolled(""); }
              }}
              onKeyDown={e => {
                if (e.key === "Enter") doSearch();
                if (e.key === "Escape") { setResults([]); setSearchDone(false); }
              }}
              placeholder="Search by name or student code…"
              style={{
                width: "100%", padding: "11px 46px 11px 16px", borderRadius: 10,
                boxSizing: "border-box", border: "2px solid rgba(3,78,123,0.18)",
                fontSize: 13, fontFamily: STATIC.font, outline: "none", background: T.inputBg, color: T.inputText, colorScheme: isDark ? "dark" : "light",
              }}
            />
            <button onClick={doSearch} disabled={searching} style={{
              position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
              width: 32, height: 32, borderRadius: 8, border: "none",
              background: STATIC.blue, color: "#fff", cursor: "pointer", WebkitTapHighlightColor:"transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {searching ? <span style={{ fontSize: 14, fontWeight: 800 }}>…</span> : <FaSearch size={12}/>}
            </button>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 14 }}>
            Showing <strong>{batch.level_name}</strong> students · Search by name to find <em>any</em> student · Click <strong>Enroll →</strong> to add
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div style={{ border: "1.5px solid rgba(3,78,123,0.12)", borderRadius: 11, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,30,100,0.08)" }}>
              {results.map((s, i) => {
                const already = enrolledIds.has(s.student_id);
                const isSaving = saving === s.student_id;
                return (
                  <div key={s.student_id} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "11px 14px",
                    borderBottom: i < results.length - 1 ? "1px solid rgba(3,78,123,0.07)" : "none",
                    background: T.glass, opacity: already ? 0.55 : 1,
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                      background: `linear-gradient(135deg,${STATIC.yellow}cc,${STATIC.yellow}66)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 900, color: STATIC.blue,
                    }}>
                      {s.first_name[0]}{s.last_name[0]}
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: STATIC.blue, fontFamily: STATIC.font, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {s.first_name} {s.last_name}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span>{s.student_code || `ID #${s.student_id}`}</span>
                        {s.level_name
                          ? <span style={{ padding: "1px 7px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: (s.color_hex || lc) + "22", color: s.color_hex || lc }}>{s.level_name}</span>
                          : <span style={{ padding: "1px 7px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: "rgba(0,0,0,0.06)", color: T.textMuted }}>No level set</span>
                        }
                        {already && <span style={{ color: "#10b981", fontWeight: 700 }}>· Already in batch</span>}
                      </div>
                    </div>
                    {/* Action */}
                    {already
                      ? (
                        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 8, background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.25)" }}>
                          <FaCheckCircle size={12} color="#10b981"/>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#10b981" }}>Enrolled</span>
                        </div>
                      ) : (
                        <button onClick={() => enroll(s)} disabled={!!saving} style={{
                          flexShrink: 0, padding: "6px 14px", borderRadius: 8, border: "none",
                          cursor: saving ? "not-allowed" : "pointer",
                          fontFamily: STATIC.font, fontSize: 12, fontWeight: 800,
                          background: isSaving ? "rgba(3,78,123,0.08)" : `linear-gradient(135deg,${STATIC.yellow},#ffe066)`,
                          color: STATIC.blue,
                          boxShadow: isSaving ? "none" : "0 2px 8px rgba(247,206,11,0.25)",
                          whiteSpace: "nowrap",
                        }}>
                          {isSaving ? "…" : "Enroll →"}
                        </button>
                      )
                    }
                  </div>
                );
              })}
            </div>
          )}

          {searchDone && results.length === 0 && (
            <div style={{ padding: "12px 14px", borderRadius: 10, background: T.glass, border: `1px solid ${T.inputBdr}`, fontSize: 12, color: T.textMuted, textAlign: "center" }}>
              {search.trim()
                ? `No students matching "${search}" available to enroll.`
                : `All ${batch.level_name || ""} students are already enrolled in this batch.`}
            </div>
          )}
        </div>

        {/* RIGHT: enrolled list */}
        <div style={{ ...card(T), overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(3,78,123,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
            <FaUsers size={12} color={STATIC.blue}/>
            <span style={{ fontSize: 12, fontWeight: 800, color: STATIC.blue, fontFamily: STATIC.font, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Enrolled Students
            </span>
            <span style={{ padding: "2px 9px", borderRadius: 10, background: `${lc}18`, color: lc, fontSize: 11, fontWeight: 700, marginLeft: 2 }}>
              {enrollments.length}{batch.max_capacity ? ` / ${batch.max_capacity}` : ""}
            </span>
          </div>

          {enrollments.length === 0 ? (
            <div style={{ padding: "52px 24px", textAlign: "center" }}>
              <FaUsers size={28} style={{ color: STATIC.blue, opacity: 0.10, display: "block", margin: "0 auto 12px" }}/>
              <div style={{ color: T.textMuted, fontSize: 13, fontFamily: STATIC.font }}>No students enrolled yet.</div>
              <div style={{ color: T.textMuted, fontSize: 12, marginTop: 4 }}>Search and add students using the form.</div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(3,78,123,0.04)" }}>
                  {["#", "Student", "Code", "Program", "Days", ""].map((h, i) => (
                    <th key={i} style={{
                      padding: "9px 14px", fontSize: 10, fontWeight: 800, color: T.textMuted,
                      textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: STATIC.font,
                      textAlign: i === 0 ? "center" : "left",
                      borderBottom: "2px solid rgba(3,78,123,0.08)", whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enrollments.map((e, i) => (
                  <tr key={e.enrollment_id}
                    style={{ borderBottom: "1px solid rgba(3,78,123,0.06)", background: i % 2 === 0 ? "transparent" : T.divider }}
                    onMouseEnter={ev => ev.currentTarget.style.background = `${STATIC.blue}05`}
                    onMouseLeave={ev => ev.currentTarget.style.background = i % 2 === 0 ? "transparent" : T.divider}
                  >
                    <td style={{ padding: "10px 14px", textAlign: "center", fontSize: 11, color: T.textMuted, width: 36 }}>{i + 1}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                          background: `linear-gradient(135deg,${STATIC.yellow}cc,${STATIC.yellow}55)`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 900, color: STATIC.blue,
                        }}>
                          {(e.student_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("")}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: STATIC.blue, fontFamily: STATIC.font }}>{e.student_name}</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: STATIC.blue }}>{e.student_code || "—"}</td>
                    <td style={{ padding: "10px 14px", fontSize: 12, color: "#555" }}>{e.program_name || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(e.days || []).map(d => (
                          <span key={d} style={{ padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: `${lc}18`, color: lc }}>{DOW[d]}</span>
                        ))}
                        {!e.days?.length && <span style={{ fontSize: 11, color: T.textMuted }}>—</span>}
                      </div>
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>
                      <button onClick={() => unenroll(e.enrollment_id, e.student_name)} style={{
                        width: 26, height: 26, borderRadius: 7, border: "1px solid #fca5a5",
                        background: "#fff5f5", color: "#ef4444", cursor: "pointer", WebkitTapHighlightColor:"transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                        onMouseEnter={ev => ev.currentTarget.style.background = "#fee2e2"}
                        onMouseLeave={ev => ev.currentTarget.style.background = "#fff5f5"}>
                        <FaTimes size={9}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — CHANGE LOG
// ══════════════════════════════════════════════════════════════════════════════
function ChangelogTab() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [logs, setLogs]     = useState([]);
  const [entity, setEntity] = useState("");
  useEffect(() => {
    const url = entity
      ? `${BASE}/batches/changelog?entity_type=${entity}&limit=100`
      : `${BASE}/batches/changelog?limit=100`;
    fetch(url).then(r => r.json()).then(d => setLogs(d || [])).catch(() => {});
  }, [entity]);
  const AC = { CREATE: "#10b981", UPDATE: "#3b82f6", DELETE: "#ef4444", UNENROLL: "#f59e0b" };
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: STATIC.font }}>Filter</label>
        <select value={entity} onChange={e => setEntity(e.target.value)}
          style={{ padding: "8px 14px", borderRadius: 9, border: `1.5px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, fontSize: 13, fontFamily: STATIC.font, colorScheme: isDark ? "dark" : "light" }}>
          <option value="">All</option>
          {["batch","enrollment","perf_category","perf_indicator","student_id_config"].map(e => (
            <option key={e} value={e}>{e.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
      </div>
      <div style={{ ...card(T), overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: STATIC.font }}>
          <thead>
            <tr style={{ background: "rgba(3,78,123,0.04)" }}>
              {["Entity","ID","Action","Changed By","Timestamp"].map((h, i) => (
                <th key={i} style={{ padding: "10px 14px", fontSize: 10, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "2px solid rgba(3,78,123,0.08)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={l.log_id} style={{ background: i % 2 === 0 ? "transparent" : T.divider, borderBottom: "1px solid rgba(3,78,123,0.06)" }}>
                <td style={{ padding: "9px 14px", color: STATIC.blue, fontWeight: 600, fontSize: 13 }}>{l.entity_type}</td>
                <td style={{ padding: "9px 14px", color: T.textMuted, fontSize: 12 }}>{l.entity_id || "—"}</td>
                <td style={{ padding: "9px 14px" }}>
                  <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: `${AC[l.action] || T.textMuted}22`, color: AC[l.action] || T.textMuted }}>{l.action}</span>
                </td>
                <td style={{ padding: "9px 14px", color: T.textSub, fontSize: 12 }}>{l.changed_by || "—"}</td>
                <td style={{ padding: "9px 14px", color: T.textMuted, fontSize: 11 }}>{l.changed_at?.replace("T"," ").slice(0,19) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && <div style={{ padding: 28, color: T.textMuted, textAlign: "center", fontSize: 13 }}>No logs found.</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export default function BatchManagement({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [tab, setTab] = useState("batches");
  const TABS = [
    { id: "batches", icon: FaLayerGroup, label: "Batches" },
    { id: "enroll",  icon: FaUserPlus,   label: "Enroll Students" },
    { id: "log",     icon: FaHistory,    label: "Change Log" },
  ];
  return (
    <div style={{ background: T.pageBg, minHeight: "100vh", padding: "28px 32px 60px", fontFamily: STATIC.font }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg,#f7ce0b,#e8b800)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 12px rgba(255,200,0,0.28)", flexShrink: 0 }}>
            <FaLayerGroup size={17} color="#02304f"/>
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: T.textPrimary, letterSpacing: "-0.02em", fontFamily: STATIC.font }}>
              Batch Management
            </h1>
            <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: 13 }}>
              Create batches with programs &amp; schedules · Enroll students in one click
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 24, background: T.glass, backdropFilter: STATIC.blur, WebkitBackdropFilter: STATIC.blur, padding: 5, borderRadius: 14, boxShadow: T.glassSh, width: "fit-content", border: T.glassBdr }}>
          {TABS.map(({ id, icon: Icon, label }) => {
            const active = tab === id;
            return (
              <button key={id} onClick={() => setTab(id)} style={{
                display: "flex", alignItems: "center", gap: 7, padding: "9px 20px", borderRadius: 10,
                border: "none", cursor: "pointer", WebkitTapHighlightColor:"transparent", fontFamily: STATIC.font, fontSize: 13, fontWeight: 700,
                transition: "all 0.18s", background: active ? "linear-gradient(135deg,#f7ce0b,#e8b800)" : "transparent",
                color: active ? "#02304f" : T.textLabel, boxShadow: active ? "0 4px 16px rgba(247,206,11,0.35)" : "none",
              }}>
                <Icon size={12}/>{label}
              </button>
            );
          })}
        </div>

        {tab === "batches" && <BatchesTab currentUserId={currentUserId}/>}
        {tab === "enroll"  && <EnrollTab  currentUserId={currentUserId}/>}
        {tab === "log"     && <ChangelogTab/>}
      </div>
    </div>
  );
}
